'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

import DynamicVariantFilters from '@/components/vehicle-variant-dynamic-filters';
import LoadingIndicator from '@/components/loading-indicator';
import MuiLegacyStyleProviders from '@/components/mui-legacy-style-providers';
import VariantFilter from '@/components/vehicle-variant-filter';
import { getVehicleVariants } from '@/utils/apollo-requests/vehicles/get-vehicles';
import constructSlug from '@/utils/construct-slug';
import { getVehicleDetailsHref } from '@/utils/get-vehicle-details-href';
import { vehicleFiltersReducer } from '@/components/vehicle-detail-header-filter/reducers';
import {
  StyledCloseButton,
  StyledContainer,
  StyledMakeModel,
  StyledModalContainer,
  StyledUpdateButton,
  StyledYearFilter,
} from '@/components/vehicle-detail-header-filter/styles';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { logTrackingEvent } from '@/utils/tracking';
import getNextVehicle from './utils/get-next-vehicle';
import getVariantCount from './utils/get-variant-count';
import type { HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';
import type { VehicleFilterAction, VehicleFilterEvent, VehicleFilterState, VehicleVariantInput } from './types';

export type VehicleVariantsProps = {
  variant: VehicleVariantInput;
  vehicles: VehicleVariantInput[];
};

export default function VehicleVariants({ variant, vehicles }: VehicleVariantsProps) {
  const router = useRouter();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const { makedescription: make, modeldescription: model, year, vehicleid } = variant;

  const pageVehicle: HsiycSearchVehiclesRecord = {
    makeDescription: make,
    modelDescription: model,
    year: String(year),
    redbookCode: vehicleid,
  };

  const pageVehicles: { vehicles: HsiycSearchVehiclesRecord[] } = {
    vehicles: vehicles.map((item) => ({
      makeDescription: item.makedescription,
      modelDescription: item.modeldescription,
      year: String(item.year),
      redbookCode: item.vehicleid,
    })),
  };

  const initialState: VehicleFilterState = {
    vehicles: [],
    staticFilter: {
      label: null,
      options: [],
    },
    dynamicFilters: [],
  };

  const typedReducer = vehicleFiltersReducer as unknown as React.Reducer<VehicleFilterState, VehicleFilterAction>;
  const [state, dispatch] = React.useReducer(typedReducer, initialState);
  const { staticFilter, dynamicFilters } = state;

  const ensureVariantsLoaded = async () => {
    if (state.vehicles.length > 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const vehicles = await getVehicleVariants({
      make,
      model,
    });

    dispatch({ type: 'FILTERS_INIT', payload: { vehicles, pageVehicle } });
    setIsLoading(false);
  };

  const handleUpdateVehicleRecord = () => {
    setIsDialogOpen(false);
    const nextVehicle = getNextVehicle(state);
    if (!nextVehicle?.year || !nextVehicle.slug) {
      return;
    }
    const { year, slug } = nextVehicle;
    const destination = getVehicleDetailsHref({
      makeSlug: constructSlug(make),
      modelSlug: constructSlug(model),
      year,
      vehicleSlug: slug,
    });
    router.push(destination);
  };

  const trackingLabel = `${make} ${model} ${year}`;

  const StyledMakeModelWithSpacing = StyledMakeModel as React.ComponentType<
    React.ComponentProps<typeof StyledMakeModel> & { hasExtraSpacing?: boolean }
  >;

  const lastDynamicFilter = dynamicFilters.at(-1);

  return (
    <MuiLegacyStyleProviders>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (open) {
            logTrackingEvent({
              event: 'change_car_variant',
              interaction_type: 'click',
              label: trackingLabel,
              variant: vehicleid,
            });
            void ensureVariantsLoaded();
          }
        }}
      >
        <DialogTrigger asChild>
          <button
            type="button"
            className={
              'flex flex-row items-center gap-3 text-primary-500 font-bold text-lg no-underline hover:underline underline-offset-4 decoration-2 cursor-pointer select-none'
            }
          >
            See all variants ({getVariantCount(pageVehicles)})
          </button>
        </DialogTrigger>

        <DialogContent
          showCloseButton={false}
          className="max-w-none w-full h-full min-h-screen rounded-none p-0 border-0 shadow-none bg-transparent"
        >
          <DialogTitle className="sr-only">
            {make} {model} variants
          </DialogTitle>
          <DialogDescription className="sr-only">
            Use the year and variant filters to select and view a different {make} {model} variant.
          </DialogDescription>
          <StyledModalContainer>
            <StyledContainer maxWidth={false}>
              <StyledCloseButton
                edge="end"
                ariaLabel="close modal"
                iconName="close"
                onClick={() => setIsDialogOpen(false)}
              />
              <StyledMakeModelWithSpacing hasExtraSpacing>
                {make} {model}
              </StyledMakeModelWithSpacing>
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <StyledYearFilter>
                    <VariantFilter
                      key={staticFilter.label ?? 'year-filter'}
                      label={staticFilter.label ?? undefined}
                      options={staticFilter.options}
                      selectedOption={staticFilter.selectedOption}
                      onChange={(event) =>
                        dispatch({
                          type: 'FILTERS_UPDATE_STATIC',
                          payload: { event: event as VehicleFilterEvent },
                        })
                      }
                      isFullWidth
                    />
                  </StyledYearFilter>
                  <DynamicVariantFilters
                    filters={dynamicFilters}
                    allVehicles={state.vehicles}
                    onChange={(event, item, index) =>
                      dispatch({
                        type: 'FILTERS_UPDATE_DYNAMIC',
                        payload: { event: event as VehicleFilterEvent, item, index: Number(index) },
                      })
                    }
                    isFullWidth
                  />
                  <StyledUpdateButton
                    onClick={handleUpdateVehicleRecord}
                    disabled={!lastDynamicFilter?.selectedOption}
                    tracking={{
                      event: 'change_car_variant',
                      interaction_type: 'confirm',
                      label: trackingLabel,
                      variant: lastDynamicFilter?.selectedOption?.value ?? '',
                    }}
                  >
                    View Variant
                  </StyledUpdateButton>
                </>
              )}
            </StyledContainer>
          </StyledModalContainer>
        </DialogContent>
      </Dialog>
    </MuiLegacyStyleProviders>
  );
}
